import {Box, TextField, Typography} from '@mui/material';
import {ReactComponent as LineImage} from '../../../assests/svg/Line.svg';
import {ReactComponent as SearchnlpImage} from '../../../assests/svg/search-nlp.svg';
import {ReactComponent as ShowmeImage} from '../../../assests/svg/show-me.svg';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import {t} from 'i18next';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import AurButton from '../../common/aur-button/AurButton';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  setIsChatPanelVisible,
  setShouldCloseAutomatically,
} from '../../../redux/slices/askLioSlice';
import {RootState} from '../../../redux/store';
import AurBanner from '../../common/aur-banner/AurBanner';
import {isEmpty} from '../../../utils/utils';
import AurPopup from '../../common/aur-popup/AurPopup';
import {setShowModal} from '../../../redux/slices/unSaveChangesModalSlice';
import AurEcaSearchLoader from '../../common/aur-eca-search-loader/AurEcaSearchLoader';
import EcaDocListGrid from '../../eca-doc-list/EcaDocListGrid';
import {useNlpSearch} from '../../../hooks/nlp-search/useNlpSearch';
import BulkTaggingStatusBanner from '../../eca-doc-list/bulk-tagging/BulkTaggingStatusBanner';
import SearchResultErrorBanner from '../index-search/SearchResultErrorBanner';
import {getIndexByClientIdAndProjectId} from '../../../api/EcaServices';
import {IndexData} from '../index-search/IndexSearchPage';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

interface LocationState {
  from: string;
  backLabel: string;
}

const INDEX_TYPE_NLP = 'NLP';
const INDEX_TYPE_STANDARD = 'STANDARD';

const NLPSearchPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {trackGAEvent} = useAnalytics();

  const [backButtonLabel, setBackButtonLabel] = useState<string>('');
  const [locationState, setLocationState] = useState<string | undefined>();
  const [isQueryTextfieldFocused, setIsQueryTextfieldFocused] = useState(false);
  const [showAskLioLink, setShowAskLioLink] = useState(false);
  const queryTextFieldRef = useRef<HTMLInputElement | null>(null);
  const [queryText, setQueryText] = useState<string>('');
  const [nlpId, setNlpId] = useState<number | undefined>();
  const [isBackNavigation, setIsBackNavigation] = useState(false);

  const {isChatPanelVisible: isLioOpen} = useSelector(
    (state: RootState) => state.askLioSlice
  );
  const {clientId} = useSelector((state: RootState) => state.selectedClient);
  const {projectId} = useSelector((state: RootState) => state.reports);
  const [stdIndexFieldTemplateId, setStdIndexFieldTemplateId] = useState<
    number | undefined
  >();

  const {
    fetchIndex,
    isIndexing,
    isLoading,
    isSearchCancelled,
    runSearch,
    searchID,
    cancelSearch,
  } = useNlpSearch(INDEX_TYPE_STANDARD);

  const fetchNlpIndex = async () => {
    let nlpIndex;
    if (clientId && projectId) {
      const response = await getIndexByClientIdAndProjectId(
        clientId,
        projectId
      );
      if (response?.status == 200) {
        if (response?.data?.data?.length) {
          const index = response.data.data.find(
            (i: IndexData) =>
              i.type.toLowerCase() == INDEX_TYPE_NLP.toLowerCase()
          );
          setNlpId(index?.fieldTemplateId);
          const standeredIndex = response.data.data.find(
            (i: IndexData) =>
              i.type.toLowerCase() == INDEX_TYPE_STANDARD.toLowerCase()
          );
          setStdIndexFieldTemplateId(standeredIndex?.fieldTemplateId);
        }
      }
    }

    return nlpIndex;
  };

  useEffect(() => {
    fetchNlpIndex();
  }, []);

  const searchCompleted = useMemo(
    () => searchID && !isSearchCancelled,
    [isSearchCancelled, searchID]
  );

  useEffect(() => {
    fetchIndex();
  }, [fetchIndex]);

  const isQueryTextFieldsCleared = useCallback(
    () => isEmpty(queryText),
    [queryText]
  );
  const onChangeQueryText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQueryText(event.target.value);
    },
    []
  );
  const onClickClearTextButton = useCallback(() => {
    setQueryText('');
  }, []);
  const showMeLines: string[] = t('nlpSearchPage.showMeLines', {
    returnObjects: true,
    defaultValue: [],
  }) as string[];
  const {showModal} = useSelector(
    (state: RootState) => state.unsavedChangesModal
  );

  const goBack = () => {
    if (isEmpty(queryText)) {
      navigate(-1);
    } else {
      setIsBackNavigation(true);

      dispatch(setShowModal(true));
    }
  };

  useEffect(() => {
    const {from, backLabel = ''} = (location?.state as LocationState) || {};
    backLabel && setBackButtonLabel(backLabel);
    if (from) {
      setLocationState(from);
    }
  }, [location?.state]);

  const {askLioQuery: askLioInlineQuery} = useSelector(
    (state: RootState) => state.askLioSlice
  );

  useEffect(() => {
    queryTextFieldRef.current?.focus();
    if (isLioOpen && !isEmpty(askLioInlineQuery))
      dispatch(setShouldCloseAutomatically(false));
    else dispatch(setShouldCloseAutomatically(true));
    setTimeout(() => {
      setShowAskLioLink(true);
      dispatch(setIsChatPanelVisible(true));
    }, 5000);
  }, [dispatch]);

  useEffect(() => {
    dispatch(setShowModal(false));
    isEmpty(queryText)
      ? localStorage.setItem('currentPath', '')
      : localStorage.setItem('currentPath', window.location.pathname);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.stopImmediatePropagation();
      // event.preventDefault()
      return false;
    };

    const handlePopState = () => {
      // Handle back button (popstate event)
      if (isEmpty(queryText)) {
        dispatch(setShowModal(false)); // Hide custom modal
        history.back();
      } else {
        setIsBackNavigation(true);
        dispatch(setShowModal(true)); // Show custom modal
        window.history.pushState(null, '', window.location.pathname); // Prevent back navigation
      }
    };

    if (!isEmpty(queryText)) {
      // Add initial state to prevent back button navigation
      window.history.pushState(null, '', window.location.pathname);
    }

    // Add event listeners for beforeunload and popstate
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, queryText]);

  const confirmationModalCloseHandler = () => {
    setIsBackNavigation(false);
    dispatch(setShowModal(false));
  };

  const confirmationModalOkHandler = () => {
    if (isBackNavigation && locationState) {
      navigate(locationState);
    } else {
      const link: string = localStorage?.getItem('currentLink') || '';
      navigate(link, {state: {source: 'nav-panel'}});
    }

    setIsBackNavigation(false);
    localStorage?.setItem('currentPath', '');
    dispatch(setShowModal(false));
  };

  const handleLoaderClose = () => {
    cancelSearch();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.nlpSearchPage.cancelThisSearch'),
        type: GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.nlpSearchPage.canceledSearch'),
      },
      subPage: {
        subpage_name: t('gaTracking.nlpSearchPage.activeSearchInProgress'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const OnClickRunSearch = (): void => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SEARCH,
      click: {
        name: t('gaTracking.nlpSearchPage.runSearch'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.nlpSearchPage.aiInvestigationBegin'),
      },
    });
    runSearch(queryText);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.nlpSearchPage.activeSearchInProgress'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  return (
    <>
      <BulkTaggingStatusBanner />
      <Box className={`nlp-search-page`}>
        <Box className="navigation">
          <Link to="#" className="back-link" onClick={goBack}>
            <AurButton
              startIcon={<AurIcon icon={IconNames.ChevronLeft} />}
              type="text"
              size="Medium"
              dataTestId="back-link"
              ariaLabel={backButtonLabel || t('common.back')}
              title={backButtonLabel || t('common.back')}
            />
            {backButtonLabel && (
              <Typography
                component="span"
                className="typo-text buttonSmall_size "
              >
                {backButtonLabel}
              </Typography>
            )}
          </Link>
        </Box>
        <Box className="main-content">
          <Box className="beta-header">
            <span className="beta-chip">
              <AurSvgIcon src="images/sparkle.svg" />
              <Typography
                component="span"
                className="typo-text label2_size badge-text"
                tabIndex={0}
              >
                {t('nlpSearchPage.beta')}
              </Typography>
            </span>
            <Typography
              className="typo-text label1_size search-text"
              tabIndex={0}
            >
              {t('nlpSearchPage.introducingInvestigativeSearch')}
            </Typography>
          </Box>
          <SearchResultErrorBanner hasFullWidth />
          <Box className="explore-content">
            <Typography
              className="typo-text display2_size"
              tabIndex={0}
              component="h1"
            >
              {t('nlpSearchPage.exploreContent')}
            </Typography>
          </Box>
          <Box className="explore-content-subheader">
            <Box className="subtext">
              <Typography className="typo-text body2_size">
                {t('nlpSearchPage.exploreContentSubheader')}
              </Typography>
            </Box>
            {!isLioOpen && showAskLioLink && (
              <Box className="ask-lio-content">
                <LineImage />
                <Box>
                  <Typography className="typo-text label1_size">
                    {t('nlpSearchPage.needHelp')}
                  </Typography>
                  <Link
                    to="#"
                    onClick={() => {
                      dispatch(setIsChatPanelVisible(!isLioOpen));
                    }}
                    className="typo-text buttonSmall_size ask-lio-link"
                  >
                    {t('nlpSearchPage.askLio')}
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
          {isIndexing && (
            <Box className="indexing-inprogress-banner">
              <AurBanner
                errorText={t('nlpSearchPage.indexingInprogress')}
                errorType="warning"
                showIcon={true}
              />
            </Box>
          )}
          <Box
            className={`nlp-search-container ${isQueryTextfieldFocused && 'focus-border'}`}
          >
            <Box className="nlp-search-header">
              <SearchnlpImage />
              <Typography className="typo-text subhead3_size">
                {t('nlpSearchPage.showmeDocuments')}
              </Typography>
            </Box>
            <TextField
              inputRef={queryTextFieldRef}
              value={queryText}
              onChange={onChangeQueryText}
              onFocus={() => setIsQueryTextfieldFocused(true)}
              onBlur={() => setIsQueryTextfieldFocused(false)}
              variant={'standard'}
              multiline
              className="nlp-search-textfield"
              minRows={1}
              maxRows={12}
              margin={'none'}
              inputProps={{
                'data-testid': 'queryTextField',
                'aria-label': `queryTextField-${queryText}`,
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>
          <Box className="nlp-search-buttons-container">
            <Box className="labels" tabIndex={0}>
              <Typography className="typo-text label1_size searchingin-color">
                {t('nlpSearchPage.searchingIn')}
              </Typography>
              <Typography className="typo-text body3_size allocuments-color">
                {t('nlpSearchPage.allDocuments')}
              </Typography>
            </Box>
            <Box className="buttons">
              <AurButton
                dataTestId="clearTextButton"
                label={t('nlpSearchPage.clearText')}
                type="exit"
                size="Medium"
                title={t('nlpSearchPage.clearText')}
                onClick={onClickClearTextButton}
              />
              <AurButton
                dataTestId="runSearchButton"
                type="continue"
                title={t('nlpSearchPage.runSearch')}
                label={t('nlpSearchPage.runSearch')}
                size="Medium"
                minWidth={180}
                disabled={isQueryTextFieldsCleared()}
                onClick={OnClickRunSearch}
              />
            </Box>
          </Box>
          {!searchCompleted && (
            <Box className="instruction-section">
              <Box>
                <Typography className="typo-text headline3_size">
                  {t('nlpSearchPage.youCanAsk')}
                </Typography>
              </Box>
              <Box className="text-container">
                <Box className="showme-section">
                  {Array.isArray(showMeLines) &&
                    showMeLines.map((line, index) => (
                      <Box className="wrapper" key={`${line}-${index}`}>
                        <Box className="showme-text">
                          <ShowmeImage />
                          <Typography className="typo-text label1_size">
                            {t('nlpSearchPage.showMe')}
                          </Typography>
                        </Box>
                        <Box className="showme-description">
                          <Typography className="typo-text body3_size text-height">
                            {line}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Box>
                <Box className="investigative-section">
                  <Box className="line-text">
                    <LineImage />
                    <Box className="header-text">
                      <Typography className="typo-text label1_size">
                        {t('nlpSearchPage.investigativeSearch')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="subheader-text">
                    <Typography className="typo-text body3_size">
                      {t('nlpSearchPage.investigativeSearchSubheader')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {searchCompleted && (
          <Box className="nlp-doc-list-container">
            <EcaDocListGrid
              data-testid="nlp-doc-list-grid"
              searchID={searchID}
              nlpIndexId={nlpId}
              isNlpSearch
              stdIndexFieldTemplateId={stdIndexFieldTemplateId}
            />
          </Box>
        )}
        {showModal && (
          <AurPopup
            className="aur-unsaved-changes-popup nlp-popup"
            open={showModal}
            onOk={confirmationModalOkHandler}
            onCancel={confirmationModalCloseHandler}
            onClose={confirmationModalCloseHandler}
            popupHeading={t(
              'nlpSearchPage.confirmationModalPopup.popupHeading'
            )}
            popupTitle={t('nlpSearchPage.confirmationModalPopup.popupTitle')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={true}
            labelActionCancel={t(
              'nlpSearchPage.confirmationModalPopup.buttonCancel'
            )}
            labelActionOk={t('nlpSearchPage.confirmationModalPopup.buttonYes')}
            cancelButtonTitle=""
          />
        )}
        {isLoading && (
          <AurEcaSearchLoader
            data-testid="aur-eca-search-loader"
            isOpen={isLoading}
            onClose={handleLoaderClose}
            keepMounted
          />
        )}
      </Box>
    </>
  );
};

export default NLPSearchPage;
